import { useEffect, useRef, useState } from "react";
import { AeonyxIcon } from "./Logo";

export default function ProductModel({
  src,
  alt,
  cameraOrbit = "20deg 78deg auto",
  autoRotate = true,
  className = "",
  background = "light", // "light" | "dark"
}) {
  const mvRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      if (window.customElements && window.customElements.get("model-viewer")) {
        setReady(true);
      } else {
        setTimeout(check, 200);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    const el = mvRef.current;
    if (!el) return;
    const onProgress = (e) => {
      const p = (e.detail && e.detail.totalProgress) || 0;
      setProgress(Math.round(p * 100));
    };
    el.addEventListener("progress", onProgress);
    return () => el.removeEventListener("progress", onProgress);
  }, [ready]);

  const loaded = progress >= 100;
  const isDark = background === "dark";

  return (
    <div
      className={`relative w-full h-full flex items-center justify-center ${className}`}
    >
      {/* Loading placeholder */}
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none ${
          isDark ? "" : "bg-gradient-to-b from-slate-50 to-slate-100"
        }`}
        style={{
          opacity: loaded ? 0 : 1,
          transition: "opacity 500ms ease-out",
        }}
      >
        <div className="relative">
          <span
            className={`absolute inset-0 rounded-full blur-2xl pulse-dot ${
              isDark ? "bg-teal-brand/25" : "bg-cyan-brand/25"
            }`}
          />
          <AeonyxIcon className="relative h-14 w-auto opacity-80" />
        </div>
        <span
          className={`font-mono-data text-[10px] uppercase tracking-[0.28em] ${
            isDark ? "text-white/60" : "text-cool-grey"
          }`}
        >
          Loading 3D
        </span>
      </div>

      {ready && (
        <model-viewer
          ref={mvRef}
          src={src}
          alt={alt}
          camera-controls=""
          {...(autoRotate ? { "auto-rotate": "" } : {})}
          rotation-per-second="18deg"
          interaction-prompt="none"
          shadow-intensity="1.0"
          shadow-softness="0.9"
          exposure="1.05"
          environment-image="neutral"
          camera-orbit={cameraOrbit}
          min-camera-orbit="auto 0deg auto"
          max-camera-orbit="auto 180deg auto"
          disable-zoom=""
          touch-action="pan-y"
          loading="eager"
          reveal="auto"
          style={{
            width: "100%",
            height: "100%",
            background: "transparent",
            position: "absolute",
            inset: 0,
            opacity: loaded ? 1 : 0,
            transition: "opacity 600ms ease-in",
          }}
        />
      )}

      {progress > 0 && progress < 100 && (
        <div
          className={`absolute bottom-3 left-1/2 -translate-x-1/2 z-10 rounded-full backdrop-blur-md px-3 py-1.5 flex items-center gap-2 ${
            isDark
              ? "bg-white/10 border border-white/15"
              : "bg-navy-deep/10 border border-navy-deep/15"
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-teal-brand pulse-dot" />
          <span
            className={`font-mono-data text-[10px] uppercase tracking-widest ${
              isDark ? "text-white/85" : "text-navy-deep/80"
            }`}
          >
            Loading · {progress}%
          </span>
        </div>
      )}
    </div>
  );
}
